/**
 * Port connection rules configuration
 * Defines fan-in/fan-out behavior for different port types
 */

// Default connection rules per port type
// fanOut: can source connect to multiple targets?
// fanIn: can target receive from multiple sources?
export const DEFAULT_PORT_CONNECTION_RULES = {
    'midi':           { fanOut: false, fanIn: false },
    'midi-rtp':       { fanOut: true,  fanIn: true  },
    'midi-usb-host':  { fanOut: false, fanIn: false },
    'midi-usb-device':{ fanOut: false, fanIn: false },
    'audio':          { fanOut: false, fanIn: false },
    'cv':             { fanOut: true,  fanIn: false },
    'gate':           { fanOut: true,  fanIn: false },
    '_default':       { fanOut: false, fanIn: false }
};

/**
 * Create a mutable copy of default rules for runtime modification
 * @returns {Object} Fresh copy of connection rules
 */
export function createConnectionRules() {
    return JSON.parse(JSON.stringify(DEFAULT_PORT_CONNECTION_RULES));
}

/**
 * Get connection rules for a specific port type
 * @param {string} portType - The port type (e.g., 'midi', 'audio', 'cv')
 * @param {Object} [rules] - Rules object to use (defaults to DEFAULT_PORT_CONNECTION_RULES)
 * @returns {Object} The rules { fanOut: boolean, fanIn: boolean }
 */
export function getConnectionRules(portType, rules = DEFAULT_PORT_CONNECTION_RULES) {
    return rules[portType] || rules['_default'];
}

/**
 * Check if two port types can connect based on signal compatibility
 * @param {string} sourceType - Source port type
 * @param {string} targetType - Target port type
 * @returns {boolean} True if connection is allowed
 */
export function canConnect(sourceType, targetType) {
    // Normalize types (handle arrays)
    const srcType = Array.isArray(sourceType) ? sourceType[0] : sourceType;
    const tgtType = Array.isArray(targetType) ? targetType[0] : targetType;
    
    // Same type always connects
    if (srcType === tgtType) return true;
    
    // Define compatible type groups
    const midiTypes = ['midi', 'midi-rtp', 'midi-usb-host', 'midi-usb-device'];
    const analogTypes = ['audio', 'cv', 'gate', 'clock'];
    
    // MIDI types can interconnect
    if (midiTypes.includes(srcType) && midiTypes.includes(tgtType)) {
        return true;
    }
    
    // Analog types can interconnect (Eurorack flexibility)
    if (analogTypes.includes(srcType) && analogTypes.includes(tgtType)) {
        return true;
    }
    
    // Otherwise, types must match
    return false;
}
